import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle, Send, LogOut, UserCircle } from 'lucide-react';
import { API_URL } from '../../constants';

const POLL_INTERVAL = 3000;

const ChatroomPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('chat_token');
  const username = localStorage.getItem('chat_username');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!token) navigate('/chatroom/login');
  }, [token, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/chat/messages/?limit=100`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data.results || []);
      setMessages(arr);
    } catch (_e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load and poll messages
  useEffect(() => {
    if (!token) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [token, fetchMessages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/chat/messages/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify({ content: input.trim() }),
      });
      if (!res.ok) throw new Error('Failed to send message.');
      setInput('');
      await fetchMessages();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('chat_token');
    localStorage.removeItem('chat_username');
    navigate('/chatroom/login');
  };

  const avatarColors = ['bg-emerald-500','bg-blue-500','bg-purple-500','bg-rose-500','bg-amber-500','bg-teal-500','bg-indigo-500','bg-pink-500'];
  const getColor = (name) => avatarColors[name.charCodeAt(0) % avatarColors.length];

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (!token) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Chat Header */}
      <div className="bg-emerald-700 text-white rounded-t-2xl px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-7 h-7" />
          <div>
            <h1 className="font-bold text-lg">Global Chatroom</h1>
            <p className="text-emerald-200 text-xs">Gbongan Community — open to all</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-emerald-200 text-sm">
            <UserCircle className="w-4 h-4" />
            <span className="font-medium text-white">{username}</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-emerald-200 hover:text-white text-sm transition">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="bg-gray-50 border-x border-gray-200 h-[60vh] overflow-y-auto px-5 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageCircle className="w-16 h-16 mb-3 opacity-20" />
            <p className="font-medium">No messages yet.</p>
            <p className="text-sm">Be the first to say hello! 👋</p>
          </div>
        )}
        {messages.map((msg, i) => {
          const isMe = msg.username === username;
          const showAvatar = !isMe && (i === 0 || messages[i-1]?.username !== msg.username);
          return (
            <div key={msg.id} className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
              {!isMe && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${getColor(msg.username || 'u')} ${showAvatar ? '' : 'invisible'}`}>
                  {(msg.username || 'U')[0].toUpperCase()}
                </div>
              )}
              <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                {showAvatar && !isMe && <p className="text-xs font-semibold text-gray-600 px-1">{msg.username}</p>}
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'}`}>
                  {msg.content}
                </div>
                <p className="text-xs text-gray-400 px-1">{formatTime(msg.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white border-x border-b border-gray-200 rounded-b-2xl px-4 py-3">
        {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
        <form onSubmit={sendMessage} className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Type a message…"
            maxLength={500}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
          />
          <button type="submit" disabled={sending || !input.trim()}
            className="bg-emerald-700 text-white px-5 py-2.5 rounded-xl hover:bg-emerald-800 transition disabled:opacity-50 flex items-center gap-2 text-sm font-medium">
            <Send className="w-4 h-4" />
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
        <p className="text-xs text-gray-400 mt-1.5 text-center">Be respectful · No spam · Community guidelines apply</p>
      </div>

      {!token && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
          <h3 className="font-bold text-gray-900 mb-2">Join the Conversation</h3>
          <p className="text-gray-600 text-sm mb-4">Register with your email to participate in the Gbongan Global Chatroom</p>
          <div className="flex gap-3 justify-center">
            <Link to="/chatroom/register" className="bg-emerald-700 text-white px-5 py-2 rounded-xl hover:bg-emerald-800 transition text-sm font-medium">Register</Link>
            <Link to="/chatroom/login" className="border border-emerald-700 text-emerald-700 px-5 py-2 rounded-xl hover:bg-emerald-50 transition text-sm font-medium">Sign In</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatroomPage;
